import React, { useState } from "react";
import { Modal, Form, Input, Button, Tabs, message, Spin } from "antd";
import { IoMegaphoneOutline } from "react-icons/io5";
import { db } from "../../firebase"; // Import Firebase config
import { collection, doc, setDoc, query, where, getDocs, limit } from "firebase/firestore";
import { v4 as uuidv4 } from "uuid"; // For unique ID generation
import { useNavigate } from "react-router-dom"; // For navigation
import "./AdvertisementSection.css";

const AdvertisementSection = () => {
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [activeTab, setActiveTab] = useState("newRegistration");
  const [formValues, setFormValues] = useState({});
  const [isLoading, setIsLoading] = useState(false); // Loading spinner
  const navigate = useNavigate();

  const handleOpenModal = () => {
    setIsModalVisible(true);
    setActiveTab("newRegistration");
    setFormValues({});
  };

  const handleCloseModal = () => setIsModalVisible(false);

  const handleFormChange = (e) => {
    setFormValues({ ...formValues, [e.target.name]: e.target.value });
  };

  // Handle new registration submission
  const handleSubmitNewRegistration = async () => {
    const uniqueId = `ADV-${uuidv4().slice(0, 8)}`;
    const { email } = formValues;
    const subject = "Advertisement Request Confirmation";
    const text = `Your unique ID is: ${uniqueId}`;

    setIsLoading(true);
    try {
      // Store data in Firestore
      await setDoc(doc(db, "advertisementRequests", uniqueId), {
        ...formValues,
        uniqueId,
        status: "Pending",
      });

      // Send confirmation email
      await fetch("https://gpa-backend-1x73.onrender.com/api/send-email", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ recipientEmail: email, subject, text }),
      });

      message.success("Registration successful! Unique ID sent via email.");
      navigate(`/Request/${uniqueId}`);
      setIsModalVisible(false);
    } catch (error) {
      console.error("Error during registration:", error);
      message.error("Registration failed. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  // Handle status check for already registered users
  const handleCheckStatus = async () => {
    const { id, email, phone } = formValues;

    setIsLoading(true);
    try {
      const q = query(
        collection(db, "advertisementRequests"),
        where("uniqueId", "==", id),
        limit(1)
      );
      const snapshot = await getDocs(q);

      if (!snapshot.empty) {
        const requestData = snapshot.docs[0].data();
        if (requestData.email === email && requestData.mobile === phone) {
          navigate(`/Request/${id}`);
        } else {
          message.error("Incorrect email or phone number.");
        }
      } else {
        message.error("No record found for the provided ID.");
      }
    } catch (error) {
      console.error("Error during status check:", error);
      message.error("Failed to verify. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="advertisement-section-contact">
      <div className="advertisement-content">
        <IoMegaphoneOutline className="advertisement-icon" />
        <h2>Promote Your Healthcare Services</h2>
        <p>
          Reach the right audience with your healthcare advertisements. Whether
          you’re offering medical consultations, wellness programs, or
          healthcare products, we provide the platform to showcase your
          services effectively.
        </p>
        <p className="slogan">"Your health, your way, your voice."</p>
        <div className="button-group">
          <button className="cta-button" onClick={handleOpenModal}>
            Contact for Advertisement
          </button>
        </div>
      </div>

      <Modal
        title="Advertisement Request"
        visible={isModalVisible}
        onCancel={handleCloseModal}
        footer={null}
      >
        <Tabs
          activeKey={activeTab}
          onChange={setActiveTab}
          centered
        >
          <Tabs.TabPane tab="New Registration" key="newRegistration">
            <Form layout="vertical" onFinish={handleSubmitNewRegistration}>
              <Form.Item label="Name of Hospital" required>
                <Input
                  name="hospitalName"
                  value={formValues.hospitalName}
                  onChange={handleFormChange}
                  placeholder="Enter hospital name"
                />
              </Form.Item>
              <Form.Item label="Name of Doctor" required>
                <Input
                  name="doctorName"
                  value={formValues.doctorName}
                  onChange={handleFormChange}
                  placeholder="Enter doctor's name"
                />
              </Form.Item>
              <Form.Item label="Email" required>
                <Input
                  name="email"
                  value={formValues.email}
                  onChange={handleFormChange}
                  placeholder="Enter email"
                  type="email"
                />
              </Form.Item>
              <Form.Item label="Mobile" required>
                <Input
                  name="mobile"
                  value={formValues.mobile}
                  onChange={handleFormChange}
                  placeholder="Enter mobile number"
                  type="tel"
                />
              </Form.Item>
              <div style={{ textAlign: "right" }}>
                <Button onClick={handleCloseModal} style={{ marginRight: "8px" }}>
                  Cancel
                </Button>
                <Button type="primary" htmlType="submit">
                  {isLoading ? <Spin size="small" /> : "Submit"}
                </Button>
              </div>
            </Form>
          </Tabs.TabPane>

          <Tabs.TabPane tab="Already Registered" key="alreadyRegistered">
            <Form layout="vertical" onFinish={handleCheckStatus}>
              <Form.Item label="Unique ID" required>
                <Input
                  name="id"
                  onChange={handleFormChange}
                  placeholder="Enter your unique ID"
                />
              </Form.Item>
              <Form.Item label="Email" required>
                <Input
                  name="email"
                  onChange={handleFormChange}
                  placeholder="Enter your email"
                  type="email"
                />
              </Form.Item>
              <Form.Item label="Phone Number" required>
                <Input
                  name="phone"
                  onChange={handleFormChange}
                  placeholder="Enter your phone number"
                  type="tel"
                />
              </Form.Item>
              <div style={{ textAlign: "right" }}>
                <Button onClick={handleCloseModal} style={{ marginRight: "8px" }}>
                  Cancel
                </Button>
                <Button type="primary" htmlType="submit">
                  {isLoading ? <Spin size="small" /> : "Check Status"}
                </Button>
              </div>
            </Form>
          </Tabs.TabPane>
        </Tabs>
      </Modal>
    </div>
  );
};

export default AdvertisementSection;
